import { ColorTypes, utils } from '@omegagrid/core';
import { FormAdapter, FormContainerOptions, FormControlProperties, FormControlType } from '@omegagrid/form';
import { msg } from '@omegagrid/localize';
import rp from '@reporting/rp-app-host';
import { url } from '@reporting/rp-mf-accounting';
import { Thread } from '@reporting/rp-types/etl';
import { delimiters, encodings, quotes } from '../types';

/**
 * The public export endpoint (rp-api, IORoute::exportData of rp-ms-etl). The thread goes by name, as in v1:
 * the id changes when a thread is duplicated, re-created or synchronized, while the customer keeps the name.
 * The server matches the name without regard to accents and case and takes the export thread first.
 */
const EXPORT_API_PATH = '/api/v2/{a}/export/{thread}';

export class ThreadApiLinkFormAdapter extends FormAdapter {

	constructor(
		private thread: Thread
	) { super() }

	/** The thread segment of the export URL: the name without accents, each part of a path-like name encoded on its own */
	static threadPath(thread: Thread): string {
		return utils.removeAccents(thread.description ?? '').split('/').map(encodeURIComponent).join('/');
	}

	getColumns() {
		return [
			{width: 130},
			{width: null},
		];
	}

	getControls(): FormControlProperties[] {
		return [
			{row: 0, col: 0, type: FormControlType.Label, value: `CSV ${msg('delimiter')}`},
			{row: 0, col: 1, type: FormControlType.List, key: 'delimiter', value: ';', content: {
				sourceData: delimiters.map(([k, v]) => ({k, v})), options: {selectMode: 'single', autoClose: true}
			}},
			{row: 1, col: 0, type: FormControlType.Label, value: `CSV ${msg('enclosure')}`},
			{row: 1, col: 1, type: FormControlType.List, key: 'quote', value: '', content: {
				sourceData: quotes.map(([k, v]) => ({k, v})), options: {selectMode: 'single', autoClose: true, allowEmpty: true}
			}},
			{row: 2, col: 0, type: FormControlType.Label, value: msg('Encoding')},
			{row: 2, col: 1, type: FormControlType.List, key: 'encoding', value: 'UTF-8', content: {
				sourceData: encodings.map(v => ({k: v, v})), options: {selectMode: 'single', autoClose: true}
			}},
			{row: 3, col: 0, type: FormControlType.Label, value: `CSV ${msg('header')}`},
			{row: 3, col: 1, type: FormControlType.Checkbox, key: 'header', value: true},
		];
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			closeAfterSave: false,
			buttons: [
				{key: 'openLink', text: msg('Open'), color: ColorTypes.Blue, icon: 'arrow-up-right-from-square'},
				{key: 'copyLink', text: msg('copy link'), color: ColorTypes.Yellow, icon: 'link'},
			]
		};
	}

	private buildUrl(): string {
		const v = (this.component?.values ?? {}) as Record<string, unknown>;
		const params = new URLSearchParams({
			csv_header: v.header ? '1' : '0',
			csv_delimiter: String(v.delimiter ?? ''),
			csv_quote: String(v.quote ?? ''),
			csv_encoding: String(v.encoding ?? ''),
		});
		const path = EXPORT_API_PATH
			.replace('{a}', String(this.thread.accounting ?? rp.accountingProvider.a))
			.replace('{thread}', ThreadApiLinkFormAdapter.threadPath(this.thread));
		return `${url.getApiBaseUrl()}${path}?${params}`;
	}

	onAction(action: string) {
		const link = this.buildUrl();
		switch (action) {
			case 'openLink':
				window.open(link, '_blank');
				break;
			case 'copyLink':
				navigator.clipboard.writeText(link);
				rp.host.showAlert({type: 'info', title: msg('API'), content: msg('Copied to clipboard')});
				break;
		}
	}
};
